'use client';

import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/platform/auth/useAuth';
import { ComposeLegalFooter } from '@/platform/legal/ComposeLegalFooter';
import { policyErrorMessage } from '@/platform/youtube/policyMessages';
import { useComposePolicy } from '@/platform/youtube/useComposePolicy';
import {
  createForumComment,
  listForumComments,
  type ForumComment,
  type ForumPost,
} from './api';
import { CommentTree } from './CommentTree';
import { feedDiscussionToggleLabel } from './feedDiscussionAccordionLabel';

type Props = {
  post: ForumPost;
  /** Product tour: no network — empty local thread. */
  tourMode?: boolean;
  onReplyCountChange?: (replyCount: number) => void;
};

export function FeedDiscussionAccordion({ post, tourMode = false, onReplyCountChange }: Props) {
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState<ForumComment[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [body, setBody] = useState('');
  const { validateTexts } = useComposePolicy();

  const loadComments = useCallback(async () => {
    if (tourMode) {
      setComments([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const cs = await listForumComments(post.id);
      setComments(cs);
      onReplyCountChange?.(cs.length);
    } catch {
      setError('Could not load replies.');
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [post.id, tourMode, onReplyCountChange]);

  useEffect(() => {
    if (!open) return;
    if (comments !== null) return;
    void loadComments();
  }, [open, comments, loadComments]);

  async function submitTopLevel(e: FormEvent) {
    e.preventDefault();
    if (!body.trim() || tourMode) return;
    const policyErr = validateTexts([body]);
    if (policyErr) {
      setError(policyErr);
      return;
    }
    try {
      await createForumComment(post.id, body.trim());
      setBody('');
      setError('');
      await loadComments();
    } catch (err) {
      setError(policyErrorMessage(err) ?? (err instanceof Error ? err.message : 'Comment failed'));
    }
  }

  async function reply(parentId: string, text: string) {
    if (tourMode) return;
    const policyErr = validateTexts([text]);
    if (policyErr) throw new Error(policyErr);
    try {
      await createForumComment(post.id, text, parentId);
      await loadComments();
    } catch (err) {
      throw new Error(policyErrorMessage(err) ?? (err instanceof Error ? err.message : 'Reply failed'));
    }
  }

  const replyCount = comments?.length ?? post.replyCount;
  const label = feedDiscussionToggleLabel(replyCount, open);

  return (
    <div className="feed-discussion">
      <button
        type="button"
        className="feed-discussion__toggle btn btn-ghost"
        aria-expanded={open}
        aria-controls={`feed-discussion-${post.id}`}
        onClick={() => setOpen((v) => !v)}
      >
        <i
          className={`uil uil-angle-down feed-discussion__chevron${open ? ' feed-discussion__chevron--open' : ''}`}
          aria-hidden
        />
        <span>{label}</span>
      </button>
      {open && (
        <div id={`feed-discussion-${post.id}`} className="feed-discussion__panel">
          {loading && <p className="text-muted">Loading replies…</p>}
          {error && <p className="form-error">{error}</p>}
          {!loading && comments && (
            <CommentTree
              comments={comments}
              postId={tourMode ? undefined : post.id}
              onReply={isAuthenticated && !tourMode ? reply : undefined}
              onUpdate={() => void loadComments()}
              canReply={isAuthenticated && !tourMode}
            />
          )}
          {!tourMode && isAuthenticated ? (
            <form onSubmit={(e) => void submitTopLevel(e)} className="feed-discussion__compose">
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Add a comment…"
                required
                rows={2}
              />
              <button type="submit" className="btn btn-primary">
                Comment
              </button>
              <ComposeLegalFooter />
            </form>
          ) : !tourMode ? (
            <p className="text-muted">Sign in to join the discussion.</p>
          ) : null}
        </div>
      )}
    </div>
  );
}
